import type { AxiosError } from 'axios';
import axios from 'axios';
import type { FC } from 'react';
import { createContext } from 'react';
import useSWR from 'swr';
import { isTokenExpired, useAuthContext } from '../../auth/AuthContext';

export type EmployeeInformation = {
  department: string;
  role: 'Executive' | 'Associate' | 'Manager';
  facility: string;
  jobFunction: string;
};

export const EmployeeContext = createContext<{
  employeeInfo:EmployeeInformation[],
  loading: boolean,
}>({
  employeeInfo: [],
  loading: true,
});

const EmployeeProvider: FC = ({ children }) => {
  const { user, accessToken, signOut } = useAuthContext();
  const userEmail = user?.email;

  const fetcher = (url: string, email: string) => axios.get<{ employeeInfo: EmployeeInformation[] }>(url, {
    params: {
      userEmail: email,
    },
  })
    .then((response) => response.data.employeeInfo)
    .catch((error: AxiosError) => {
      // Only sign out when our token has genuinely expired. Signing out on any
      // 401/403 logged markers out mid-shift on transient server errors, and
      // sent no-role accounts into a login loop; other failures are surfaced
      // as SWR errors and retried on the next refresh interval.
      if (error?.response?.status === 401 && (!accessToken || isTokenExpired(accessToken))) {
        signOut();
        return undefined;
      }
      throw error;
    });

  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
  const { data, error } = useSWR((userEmail && userEmail !== '') ? ['/EmployeeInfo/GetEmployeeRoleDepartmentFacility', userEmail] : null,
    (url, email) => fetcher(url, email), {
      refreshInterval: 60000,
      refreshWhenHidden: false,
      revalidateOnReconnect: false,
      refreshWhenOffline: false,
    });

  return (
    <EmployeeContext.Provider value={{
      employeeInfo: data || [],
      // A failed fetch must end the "Logging in..." state instead of leaving
      // the user on an endless spinner.
      loading: Boolean(!data && !error),
    }}
    >
      {children}
    </EmployeeContext.Provider>
  );
};

export default EmployeeProvider;
